import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePMContext } from '../../contexts/PMContext';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { useUserContext } from '../../contexts/UserContext';
import { useCompanyServiceCenters } from '../../hooks/useCompanyServiceCenters';
import { applyServiceQueueFilters, readPmQueueView, splitPmCategoryNames, writePmQueueView, type ServiceQueueEntry } from '../../utils/pmFilterUtils';
import { activeServiceCenters } from '../../utils/serviceCenterUtils';
import { pmSchedulingUM } from '../pages-equipment/equipmentFieldUtils';
import { getNextServiceInfo, serviceLevelsOnSchedulingScale } from '../../utils/pmUtils';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { CLOUDSCAPE_MOBILE_BREAKPOINT } from '../../utils/constants';
import { PMStatus } from '../../types/pmTypes';
import { SingleUnit } from '../pages-equipment/equipmentTypes';
import {
    PM_FILTER_ALL,
    PM_QUEUE_MOBILE_PAGE_SIZE,
    PM_QUEUE_PAGE_SIZE,
} from './pmConstants';
import '../../css/PMDashboard.css';
import { Btn, Card, DataTable, Pagination, Select, Tooltip } from '../ui';
import type { DataTableColumn, SelectOption } from '../ui';
import CardHeader from '../ui/headers/CardHeader';

interface ServiceQueueRow extends ServiceQueueEntry {
    unitId: string;
    serviceLevel: string;
    currentUM: number;
    currentUMLabel: string;
    targetUM: number;
    targetUMLabel: string;
    status: PMStatus;
}

interface PMServiceQueueProps {
    onSelectUnit?: (unitId: string) => void;
}

const getStatusClass = (status: PMStatus): string => {
    switch (status) {
        case PMStatus.OVERDUE:
            return 'overdue';
        case PMStatus.DUE_SOON:
            return 'due-soon';
        default:
            return 'scheduled';
    }
};

const getStatusLabel = (status: PMStatus): string => {
    switch (status) {
        case PMStatus.DUE_SOON:
            return 'Due soon';
        case PMStatus.OVERDUE:
            return 'Overdue';
        case PMStatus.SCHEDULED:
            return 'Scheduled';
        default:
            return status;
    }
};

const CONFIGURE_HINT = 'Click the row to open PM Equipment Configuration for this equipment.';

const getStatusTooltip = (status: PMStatus): string | undefined => {
    switch (status) {
        case PMStatus.OVERDUE:
            return `The current reading is at or past the next service target. ${CONFIGURE_HINT}`;
        case PMStatus.DUE_SOON:
            return `The current reading is inside the warning window before the next service target. ${CONFIGURE_HINT}`;
        default:
            return undefined;
    }
};

const FilterFunnelIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="icon pm-queue-filter-icon"
        aria-hidden="true"
    >
        <path
            d="M1.5 2.25h13l-4.75 5.7v4.3L6.25 11V7.95L1.5 2.25Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
    </svg>
);

const ALL_OPTION: SelectOption = { value: PM_FILTER_ALL, label: 'Show all' };

const toFilterOptions = (values: string[]): SelectOption[] => [
    ALL_OPTION,
    ...values.map((value) => ({ value, label: value })),
];

function useIsMobileViewport() {
    const [isMobile, setIsMobile] = useState(
        () =>
            typeof window !== 'undefined' &&
            window.matchMedia(`(max-width: ${CLOUDSCAPE_MOBILE_BREAKPOINT}px)`).matches,
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${CLOUDSCAPE_MOBILE_BREAKPOINT}px)`);
        const onChange = () => setIsMobile(mediaQuery.matches);
        mediaQuery.addEventListener('change', onChange);
        return () => mediaQuery.removeEventListener('change', onChange);
    }, []);

    return isMobile;
}

const formatUmReading = (value: number, unitOfMeasure: string): string => {
    const unitLabel = displayUnitOfMeasure(unitOfMeasure);
    return unitLabel ? `${value.toLocaleString()} ${unitLabel}` : value.toLocaleString();
};

/**
 * PM Service Queue table with columns: Equipment, Category, Location,
 * Service Level, Current UM, Target UM, Service Center, Status.
 * Only Overdue and Due Soon units are listed. Scheduled equipment stays off the queue.
 * Desktop: category / service level / status filters and pagination.
 * Mobile: Equipment / Category / Status, Filters toggle, and Show more.
 *
 * Validates: Requirements 7.2, 7.3, 7.4, 2.3, 2.4, 3.5
 */
export const PMServiceQueue = ({ onSelectUnit }: PMServiceQueueProps) => {
    const { pmConfigurations, enrolledUnits, getStatusForUnit } = usePMContext();
    const { companyLocations } = useRegionAndLocationContext();
    const { company, companyOwner, companyUser } = useUserContext();
    const companyId = (company?.id as string) || companyOwner?.companyId || (companyUser?.companyId as string) || '';
    const { serviceCenters } = useCompanyServiceCenters(companyId);
    const isMobile = useIsMobileViewport();
    const [categoryFilter, setCategoryFilter] = useState(PM_FILTER_ALL);
    const [serviceCenterFilter, setServiceCenterFilter] = useState(PM_FILTER_ALL);
    const [serviceLevelFilter, setServiceLevelFilter] = useState(PM_FILTER_ALL);
    const [statusFilter, setStatusFilter] = useState(PM_FILTER_ALL);
    const [queueViewReady, setQueueViewReady] = useState(false);
    const restoredQueueCompany = useRef('');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [visibleCount, setVisibleCount] = useState(PM_QUEUE_MOBILE_PAGE_SIZE);

    const locationNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        companyLocations.forEach((loc) => {
            map[loc.id] = loc.name;
        });
        return map;
    }, [companyLocations]);

    const serviceCenterNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        serviceCenters.forEach((center) => {
            if (center.id && center.name) {
                map[center.id] = center.name;
            }
        });
        return map;
    }, [serviceCenters]);

    const allRows: ServiceQueueRow[] = useMemo(() => {
        return (enrolledUnits as SingleUnit[]).map((unit) => {
            const config = pmConfigurations.find((c) => c.unitId === unit.id);
            const currentUM = pmSchedulingUM(unit);
            const umType = config?.unitOfMeasure ?? unit.unitOfMeasure ?? '';
            const serviceLevels = serviceLevelsOnSchedulingScale(
                config?.serviceLevels ?? [],
                unit.replacedUMCounterValue,
            );
            const status = getStatusForUnit(unit.id);

            const nextService = getNextServiceInfo(currentUM, serviceLevels);
            const dueLevelNames = nextService?.dueLevels
                .map((sl) => sl.levelIdentifier)
                .filter(Boolean)
                .join(', ');

            const locationName = unit.locationId ? (locationNameMap[unit.locationId] ?? '') : '';
            const serviceCenterName = unit.serviceCenterId
                ? (serviceCenterNameMap[unit.serviceCenterId] ?? '')
                : '';

            return {
                unitId: unit.id,
                equipmentName: unit.name ?? '',
                category: unit.category?.name ?? '',
                location: locationName,
                serviceCenter: serviceCenterName,
                serviceLevel: dueLevelNames || '—',
                currentUM,
                currentUMLabel: formatUmReading(currentUM, umType),
                targetUM: nextService?.targetUM ?? 0,
                targetUMLabel: nextService ? formatUmReading(nextService.targetUM, umType) : '—',
                status,
            };
        });
    }, [enrolledUnits, pmConfigurations, getStatusForUnit, locationNameMap, serviceCenterNameMap]);

    const queueRows = useMemo(
        () => allRows.filter((row) => row.status === PMStatus.OVERDUE || row.status === PMStatus.DUE_SOON),
        [allRows],
    );

    const categoryOptions = useMemo(() => {
        const names = (rows: ServiceQueueRow[]) => rows.map((row) => row.category);
        const { due, rest } = splitPmCategoryNames(names(queueRows), names(allRows));
        const restStartsAfterDue = due.length > 0 && rest.length > 0;
        return [
            ALL_OPTION,
            ...due.map((name) => ({ value: name, label: name })),
            ...rest.map((name, index) => ({
                value: name,
                label: name,
                dividerBefore: restStartsAfterDue && index === 0,
            })),
        ];
    }, [allRows, queueRows]);
    const serviceCenterOptions = useMemo(
        () =>
            toFilterOptions([
                ...new Set(
                    activeServiceCenters(serviceCenters)
                        .map((center) => center.name?.trim() || '')
                        .filter(Boolean),
                ),
            ]),
        [serviceCenters],
    );
    const serviceLevelOptions = useMemo(
        () =>
            toFilterOptions(
                [...new Set(queueRows.map((row) => row.serviceLevel).filter((level) => level && level !== '—'))].sort(),
            ),
        [queueRows],
    );
    const statusOptions = useMemo<SelectOption[]>(
        () => [
            ALL_OPTION,
            { value: PMStatus.OVERDUE, label: 'Overdue' },
            { value: PMStatus.DUE_SOON, label: 'Due Soon' },
        ],
        [],
    );

    const filteredRows = useMemo(
        () =>
            applyServiceQueueFilters(queueRows, {
                category: categoryFilter,
                serviceCenter: serviceCenterFilter,
                serviceLevel: serviceLevelFilter,
                status: statusFilter,
            }),
        [queueRows, categoryFilter, serviceCenterFilter, serviceLevelFilter, statusFilter],
    );

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / PM_QUEUE_PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * PM_QUEUE_PAGE_SIZE;
    const pageRows = isMobile
        ? filteredRows.slice(0, visibleCount)
        : filteredRows.slice(startIdx, startIdx + PM_QUEUE_PAGE_SIZE);

    const resetPaging = () => {
        setCurrentPage(1);
        setVisibleCount(PM_QUEUE_MOBILE_PAGE_SIZE);
    };

    useEffect(() => {
        if (!companyId || restoredQueueCompany.current === companyId) return;
        const saved = readPmQueueView(window.localStorage, companyId);
        setCategoryFilter(saved.category);
        setServiceCenterFilter(saved.serviceCenter);
        setServiceLevelFilter(saved.serviceLevel);
        setStatusFilter(saved.status);
        restoredQueueCompany.current = companyId;
        setQueueViewReady(true);
    }, [companyId]);

    useEffect(() => {
        if (!companyId || !queueViewReady) return;
        writePmQueueView(window.localStorage, companyId, {
            category: categoryFilter,
            serviceCenter: serviceCenterFilter,
            serviceLevel: serviceLevelFilter,
            status: statusFilter,
        });
    }, [companyId, queueViewReady, categoryFilter, serviceCenterFilter, serviceLevelFilter, statusFilter]);

    useEffect(() => {
        if (!queueViewReady || serviceCenters.length === 0) return;
        const known = new Set(serviceCenterOptions.map((option) => option.value));
        if (serviceCenterFilter !== PM_FILTER_ALL && !known.has(serviceCenterFilter)) {
            setServiceCenterFilter(PM_FILTER_ALL);
        }
    }, [queueViewReady, serviceCenterFilter, serviceCenterOptions, serviceCenters.length]);

    const columns = useMemo<DataTableColumn<ServiceQueueRow>[]>(
        () => [
            {
                id: 'equipmentName',
                header: 'Equipment',
                sortable: true,
                sortType: 'alpha',
                sortValue: (row) => row.equipmentName,
                mobileCardPrimary: true,
                tdClassName: 'pm-equipment-name',
                renderCell: (row) => row.equipmentName,
            },
            {
                id: 'category',
                header: 'Category',
                sortable: true,
                sortType: 'alpha',
                sortValue: (row) => row.category,
                renderCell: (row) => row.category || '—',
            },
            {
                id: 'status',
                header: 'Status',
                sortable: true,
                sortType: 'alpha',
                sortValue: (row) => row.status,
                renderCell: (row) => {
                    const label = (
                        <span className={`pm-status-link ${getStatusClass(row.status)}`}>
                            {getStatusLabel(row.status)}
                        </span>
                    );
                    const tip = getStatusTooltip(row.status);
                    if (!tip) return label;
                    return <Tooltip content={tip} focusable={false}>{label}</Tooltip>;
                },
            },
            {
                id: 'location',
                header: 'Location',
                sortable: true,
                sortType: 'alpha',
                sortValue: (row) => row.location,
                thClassName: 'pm-queue-col-wide pm-queue-col-location',
                tdClassName: 'pm-queue-col-wide pm-queue-col-location',
                renderCell: (row) => row.location || '—',
            },
            {
                id: 'serviceLevel',
                header: 'Service Level',
                sortable: true,
                sortType: 'alpha',
                sortValue: (row) => row.serviceLevel,
                thClassName: 'pm-queue-col-wide',
                tdClassName: 'pm-queue-col-wide',
                renderCell: (row) => row.serviceLevel,
            },
            {
                id: 'currentUM',
                header: 'Current UM',
                sortable: true,
                sortType: 'numeric',
                sortValue: (row) => row.currentUM,
                thClassName: 'pm-queue-col-wide',
                tdClassName: 'pm-queue-col-wide',
                renderCell: (row) => row.currentUMLabel,
            },
            {
                id: 'targetUM',
                header: 'Target UM',
                sortable: true,
                sortType: 'numeric',
                sortValue: (row) => row.targetUM,
                thClassName: 'pm-queue-col-wide',
                tdClassName: 'pm-queue-col-wide',
                renderCell: (row) => row.targetUMLabel,
            },
            {
                id: 'serviceCenter',
                header: 'Service Center',
                sortable: true,
                sortType: 'alpha',
                sortValue: (row) => row.serviceCenter || '',
                thClassName: 'pm-queue-col-wide pm-queue-col-service-center',
                tdClassName: 'pm-queue-col-wide pm-queue-col-service-center',
                renderCell: (row) => row.serviceCenter || '—',
            },
        ],
        [],
    );

    const handleRowClick = useCallback(
        (row: ServiceQueueRow) => {
            onSelectUnit?.(row.unitId);
        },
        [onSelectUnit],
    );

    const hasActiveFilters =
        categoryFilter !== PM_FILTER_ALL ||
        serviceCenterFilter !== PM_FILTER_ALL ||
        serviceLevelFilter !== PM_FILTER_ALL ||
        statusFilter !== PM_FILTER_ALL;
    const emptyMessage = hasActiveFilters ? 'No equipment matches your filters' : 'No overdue or due soon equipment';

    const filterControls = (
        <div className="pm-queue-filters">
            <label className="pm-queue-filter">
                <span className="pm-queue-filter-label">Category</span>
                <Select
                    options={categoryOptions}
                    value={categoryFilter}
                    onChange={(value) => {
                        setCategoryFilter(value);
                        resetPaging();
                    }}
                    ariaLabel="Filter by category"
                />
            </label>
            <label className="pm-queue-filter">
                <span className="pm-queue-filter-label">Service Level</span>
                <Select
                    options={serviceLevelOptions}
                    value={serviceLevelFilter}
                    onChange={(value) => {
                        setServiceLevelFilter(value);
                        resetPaging();
                    }}
                    ariaLabel="Filter by service level"
                />
            </label>
            <label className="pm-queue-filter">
                <span className="pm-queue-filter-label">Service Center</span>
                <Select
                    options={serviceCenterOptions}
                    value={serviceCenterFilter}
                    onChange={(value) => {
                        setServiceCenterFilter(value);
                        resetPaging();
                    }}
                    ariaLabel="Filter by service center"
                />
            </label>
            <label className="pm-queue-filter">
                <span className="pm-queue-filter-label">Status</span>
                <Select
                    options={statusOptions}
                    value={statusFilter}
                    onChange={(value) => {
                        setStatusFilter(value);
                        resetPaging();
                    }}
                    ariaLabel="Filter by status"
                />
            </label>
        </div>
    );

    return (
        <Card className="card-lg pm-service-queue-panel">
            <div className="pm-queue-header">
                <CardHeader
                    as="h3"
                    title="PM Service Queue"
                    paddingVariant="pad-lg"
                    actions={
                        isMobile ? (
                            <Btn
                                variant="secondary-outlined"
                                buttonSize="sm"
                                buttonShape="pill"
                                buttonClasses="pm-queue-filters-toggle"
                                buttonIcon={<FilterFunnelIcon />}
                                buttonText="Filters"
                                buttonOnClick={() => setFiltersOpen((open) => !open)}
                                buttonAriaProps={{ 'aria-expanded': filtersOpen }}
                            />
                        ) : undefined
                    }
                />
                <div className={`pm-queue-toolbar${isMobile && filtersOpen ? ' is-open' : ''}`}>
                    {filterControls}
                    {!isMobile && filteredRows.length > PM_QUEUE_PAGE_SIZE && (
                        <Pagination
                            className="pm-pagination"
                            currentPage={safePage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            ariaLabel="PM service queue pagination"
                        />
                    )}
                </div>
            </div>

            <DataTable
                caption="PM Service Queue"
                captionVisuallyHidden
                tableClassName="pm-table"
                scrollRegionLabel="PM service queue table"
                columns={columns}
                rows={pageRows}
                getRowKey={(row) => row.unitId}
                mobileCardLayout={false}
                onRowClick={onSelectUnit ? handleRowClick : undefined}
                emptyState={<div className="pm-table-empty">{emptyMessage}</div>}
            />

            {isMobile && visibleCount < filteredRows.length && (
                <div className="pm-queue-show-more-wrap">
                    <Btn
                        variant="warning-outlined"
                        buttonShape="pill"
                        buttonText="Show more"
                        buttonOnClick={() => setVisibleCount((count) => count + PM_QUEUE_MOBILE_PAGE_SIZE)}
                    />
                </div>
            )}
        </Card>
    );
};
